import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Td, Tr } from '@chakra-ui/react';
import React, { useEffect, useState, useRef } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import {
  useTrDraggableHandleWithPanelContext,
  TrDraggableHandleWithPanelProvider,
} from '../hooks/TrDraggableRowWithPanelContextProvder';
import * as types from '../types';
import { TrDraggableHandle } from './TrDraggableHandle';
import { IconButton } from 'packages/common-frontend/components/IconButton';

type TrDraggableHandleWithPanelProps = types.TrDraggableHandleWithPanel.ParentProps & {
  children: (input: {
    isPanelOpen: boolean;
    setIsPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }) => React.ReactNode;
};
export const TrDraggableHandleWithPanel = ({
  children,
  ...props
}: TrDraggableHandleWithPanelProps) => {
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const rowChildrenCount = useRef<number>(0);

  return (
    <TrDraggableHandleWithPanelProvider
      passedProps={{
        ...props,
        isPanelOpen,
        setIsPanelOpen,
        rowChildrenCount,
      }}
    >
      {children({ isPanelOpen, setIsPanelOpen })}
    </TrDraggableHandleWithPanelProvider>
  );
};
const Row = ({ children }: { children: React.ReactNode }) => {
  const rowContext = useTrDraggableHandleWithPanelContext();
  finleyAssert(rowContext, 'assume context is here');
  const { TrDraggableHandleWithPanelChildrenProps } = rowContext;
  finleyAssert(
    TrDraggableHandleWithPanelChildrenProps,
    'TrDraggableHandleWithPanelContext context exists',
  );

  const {
    id,
    acceptedType,
    isDragDisabled,
    index,
    isPanelOpen,
    setIsPanelOpen,
    moveRow,
    rowChildrenCount,
  } = TrDraggableHandleWithPanelChildrenProps;

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    rowChildrenCount.current = React.Children.count(children);
  }, [children, rowChildrenCount]);

  return (
    <TrDraggableHandle
      id={id}
      index={index}
      acceptedType={acceptedType}
      isDragDisabled={isDragDisabled}
      moveRow={moveRow}
    >
      {children}
      {isDragDisabled && (
        <Td borderBottom={isPanelOpen && isDragDisabled ? 'none' : '1px'}>
          <IconButton
            ariaLabel={isPanelOpen ? 'Collapse details' : 'Expand details'}
            label={isPanelOpen ? 'Collapse details' : 'Expand details'}
            icon={isPanelOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            onClick={() => {
              setIsPanelOpen((oldIsPanelOpen: boolean) => !oldIsPanelOpen);
            }}
            isDisabled={!isDragDisabled}
          />
        </Td>
      )}
    </TrDraggableHandle>
  );
};
Row.displayName = 'TrDraggableHandleWithPanel.Row';
TrDraggableHandleWithPanel.Row = Row;

type PanelProps = types.TrDraggableHandleWithPanel.Panel.Props & {
  children: React.ReactNode;
};
const Panel = ({ isPanelDisabled, children }: PanelProps) => {
  const context = useTrDraggableHandleWithPanelContext();
  finleyAssert(context, 'assume context is here');
  const { TrDraggableHandleWithPanelChildrenProps } = context;
  finleyAssert(
    TrDraggableHandleWithPanelChildrenProps,
    'assume context is here',
  );

  const {
    isPanelOpen,
    rowChildrenCount,
  } = TrDraggableHandleWithPanelChildrenProps;

  return (
    <Tr
      borderTop="none"
      display={isPanelOpen && !isPanelDisabled ? undefined : 'none'}
    >
      <Td colSpan={rowChildrenCount.current + 1} minHeight="35px">
        {children}
      </Td>
    </Tr>
  );
};
Panel.displayName = 'TrDraggableHandleWithPanel.Panel';
TrDraggableHandleWithPanel.Panel = Panel;
